import { invertedEffects } from "@codemirror/commands";
import { ChangeDesc, EditorSelection, StateEffect, StateField } from "@codemirror/state";
import { Decoration, DecorationSet, EditorView } from "@codemirror/view";
import { TextDecorations } from "@quickpoint.me/shared";

const addUnderline = StateEffect.define<{ from: number; to: number }>({
  map: mapRange,
});
const removeUnderline = StateEffect.define<{ from: number; to: number }>({
  map: mapRange,
});

function mapRange(range: { from: number; to: number }, change: ChangeDesc) {
  const from = change.mapPos(range.from), to = change.mapPos(range.to);
  return from < to ? { from, to } : undefined;
}

export function underlineSelection(view: EditorView) {
  const textModifier = TextDecorations.bold;
  const changes = view.state.changeByRange(range => {
    return {
      changes: [{ from: range.from, insert: textModifier }, { from: range.to, insert: textModifier }],
      range: EditorSelection.range(range.from, range.to + (textModifier.length * 2)),
      effects: addUnderline.of({ from: range.from, to: range.to + (textModifier.length * 2) }),
    };
  });

  view.dispatch(changes);
  return true;
}

export function unUnderlineSelection(view: EditorView) {
  const underlined = view.state.field(underlinedRanges);
  const effects: StateEffect<any>[] = [];
  for (const sel of view.state.selection.ranges) {
    underlined.between(sel.from, sel.to, (rFrom, rTo) => {
      const from = Math.max(sel.from, rFrom), to = Math.min(sel.to, rTo);
      if (from < to) effects.push(removeUnderline.of({ from, to }));
    });
  }
  view.dispatch({ effects });
  return true;
}

const underline = Decoration.mark({ class: "cm-underline" });

const underlinedRanges = StateField.define({
  create() {
    return Decoration.none;
  },
  update(ranges, tr) {
    ranges = ranges.map(tr.changes);
    for (let e of tr.effects) {
      if (e.is(addUnderline)) {
        ranges = addRange(ranges, e.value);
      } else if (e.is(removeUnderline)) {
        ranges = cutRange(ranges, e.value);
      }
    }
    return ranges;
  },
  provide: field => EditorView.decorations.from(field),
});

// !cutRange

function cutRange(ranges: DecorationSet, r: { from: number; to: number }) {
  const leftover: any[] = [];
  ranges.between(r.from, r.to, (from, to, deco) => {
    if (from < r.from) leftover.push(deco.range(from, r.from));
    if (to > r.to) leftover.push(deco.range(r.to, to));
  });
  return ranges.update({
    filterFrom: r.from,
    filterTo: r.to,
    filter: () => false,
    add: leftover,
  });
}

function addRange(ranges: DecorationSet, r: { from: number; to: number }) {
  ranges.between(r.from, r.to, (from, to) => {
    if (from < r.from) r = { from, to: r.to };
    if (to > r.to) r = { from: r.from, to };
  });
  return ranges.update({
    filterFrom: r.from,
    filterTo: r.to,
    filter: () => false,
    add: [underline.range(r.from, r.to)],
  });
}

const invertUnderline = invertedEffects.of(tr => {
  const found = [];
  for (let e of tr.effects) {
    if (e.is(addUnderline)) found.push(removeUnderline.of(e.value));
    else if (e.is(removeUnderline)) found.push(addUnderline.of(e.value));
  }
  const ranges = tr.startState.field(underlinedRanges);
  tr.changes.iterChangedRanges((chFrom, chTo) => {
    ranges.between(chFrom, chTo, (rFrom, rTo) => {
      if (rFrom >= chFrom || rTo <= chTo) {
        let from = Math.max(chFrom, rFrom), to = Math.min(chTo, rTo);
        if (from < to) found.push(addUnderline.of({ from, to }));
      }
    });
  });
  return found;
});

export function rangeUnderlining() {
  return [
    underlinedRanges,
    invertUnderline,
  ];
}
